import { ASK_QUESTION_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { RovoLabsConfirmedTools } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type Conversation,
	isStoreAssistant,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

type IsAnsweredFirstRequestAifcQuestionParams = {
	messages: Conversation['messages'];
	confirmedTools: RovoLabsConfirmedTools;
	isStagingArea: boolean;
	productKey: string | undefined;
};

/**
 * Whether a confirmed-tools send is answering an ask_question that was returned for the first
 * request in the AIFC (Create with Rovo) modal.
 *
 * "First request" means the user hasn't sent another message yet (one human message in the
 * conversation), so a chain of clarifying questions answered back-to-back still counts.
 */
export const isAnsweredFirstRequestAifcQuestion = ({
	messages,
	confirmedTools,
	isStagingArea,
	productKey,
}: IsAnsweredFirstRequestAifcQuestionParams): boolean => {
	if (productKey !== 'confluence' || !isStagingArea) {
		return false;
	}

	const wasAskQuestionAnswered = confirmedTools.tools.some(
		(tool) => tool.key === ASK_QUESTION_KEY && tool.resolution?.type !== 'cancelled',
	);
	if (!wasAskQuestionAnswered) {
		return false;
	}

	const humanMessageCount = messages.filter(isStoreHuman).length;
	if (humanMessageCount !== 1) {
		return false;
	}

	const confirmedMessage = messages
		.filter(isStoreAssistant)
		.find((message) => String(message.content?.message?.message?.id) === confirmedTools.message_id);
	if (!confirmedMessage) {
		return false;
	}

	return (
		confirmedMessage.content?.message?.message?.actions?.some(
			(action) => action.key === ASK_QUESTION_KEY,
		) ?? false
	);
};
